import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import {
  Button,
  DataTable,
  Dialog,
  EmptyState,
  Field,
  Icon,
  Input,
  Pagination,
  Panel,
  Tag,
  useErrorDialog,
  useToast,
  type Column,
} from '@dayon/design-system';
import { formatCount } from '@dayon/money';
import {
  AUDIT_REASON_MIN_LENGTH,
  type PageMeta,
  type ParkedNotificationRow,
} from '@dayon/contracts';
import { api, ApiRequestError } from '../../lib/api.ts';
import { reasonProblem, reasonReady } from './payout-state.ts';

/**
 * Player funds → Parked notifications: what StarPay sent that the platform filed for a person.
 *
 * THE PAYOUT DESK'S (ruling 173), so it lives on the payout desk's screen, in the tab the Payout
 * provider tile opens:
 *   GET  /api/platform/webhooks/parked      newest first, paged, behind transactions.view — the key
 *                                           the funds screen reads with — and with the brand chosen
 *                                           on this screen, as the tile's count is read
 *   POST /api/platform/webhooks/:id/seen    { reason }, behind funds.approve, the payout desk's
 *                                           deciding key
 *
 * A notification the platform cannot confirm is filed for a person, and the Payout provider tile
 * counts the ones still waiting — exactly the waiting rows this list shows the same reader. Two kinds,
 * and each has its own way out:
 *   · one about a payout the notification left waiting for an operator waits WITH that payout: it
 *     clears when the payout's hold is settled on Player funds, never by hand, so its row links to
 *     the withdrawal and offers nothing else;
 *   · any other — one that named no payout (a probe disbursement on StarPay's UAT files one), one
 *     about a payout it left as it was — waits on its own until somebody marks it seen, with a reason
 *     that goes on the audit trail. Marking it moves no money and changes no payout.
 *
 * BRAND-AWARE BOTH WAYS. One about a payout is listed within the reader's brands, as the payout is;
 * one that names no payout belongs to no brand, and is listed (and marked) only for a role that sees
 * every brand. A role restricted to some brands is told so, rather than left to wonder why a notice a
 * colleague mentioned is not here.
 *
 * THE NOTE IS THE PLATFORM'S OWN SENTENCE. The inbox keeps an error text beside it that can hold an
 * exception's own message; the route never sends it, and nothing here would print it.
 *
 * A READ THAT FAILED IS NOT AN EMPTY LIST, and a role without the key is not asked at all: the read
 * is gated by the effect, not only by what is painted.
 */

const PAGE_SIZE = 25;

/**
 * "12 Oct 09:14" in Manila time — the platform's clock, the one the gaming day and the rest of the
 * console read in, so a notification's time lines up with the times beside it.
 */
const received = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: 'Asia/Manila',
});
const at = (iso: string): string => received.format(new Date(iso)).replace(',', '');

/** Where a notification stands, in a few words. */
function standingOf(r: ParkedNotificationRow): { words: string; waiting: boolean } {
  if (r.waiting) {
    return {
      words: r.withdrawalRef === null ? 'Waiting for a person' : 'Waiting with its payout',
      waiting: true,
    };
  }
  if (r.seenAt !== null) {
    return {
      words: `Seen by ${r.seenBy ?? 'an admin no longer on the platform'} · ${at(r.seenAt)}`,
      waiting: false,
    };
  }
  return { words: 'Cleared when its payout’s hold was settled', waiting: false };
}

/** A matched notification's link: the funds screen opens that withdrawal's drawer from it. */
export const withdrawalLink = (ref: string): string =>
  `/funds?tab=withdrawals&withdrawal=${encodeURIComponent(ref)}`;

/**
 * The list itself. "Mark as seen" only on a notification that waits on its own — one with no
 * payout to clear with; a matched one links to its withdrawal instead.
 */
export function ParkedNotificationsTable({
  rows,
  canMark,
  onMark,
  empty,
}: {
  rows: ParkedNotificationRow[];
  canMark: boolean;
  onMark: (row: ParkedNotificationRow) => void;
  empty?: ReactNode;
}) {
  const columns: Column<ParkedNotificationRow>[] = [
    {
      key: 'received',
      header: 'Received',
      render: (r) => <span className="tabular">{at(r.receivedAt)}</span>,
    },
    { key: 'note', header: 'What the platform found', render: (r) => r.note },
    {
      key: 'payout',
      header: 'Payout',
      render: (r) =>
        r.withdrawalRef === null ? (
          <span className="text-subtle">no matching payout</span>
        ) : (
          <Link
            to={withdrawalLink(r.withdrawalRef)}
            aria-label={`Open ${r.withdrawalRef} on Player funds`}
          >
            {r.withdrawalRef}
          </Link>
        ),
    },
    {
      key: 'standing',
      header: 'Standing',
      render: (r) => {
        const standing = standingOf(r);
        return <Tag tone={standing.waiting ? 'accent' : 'quiet'}>{standing.words}</Tag>;
      },
    },
    {
      key: 'action',
      header: 'Action',
      render: (r) =>
        r.waiting && r.withdrawalRef === null ? (
          canMark ? (
            <div className="row-actions">
              <Button
                small
                onClick={() => onMark(r)}
                aria-label={`Mark the notification received ${at(r.receivedAt)} as seen`}
              >
                Mark as seen
              </Button>
            </div>
          ) : (
            <span className="text-subtle">Marking it needs funds.approve</span>
          )
        ) : r.waiting ? (
          <span className="text-subtle">Clears when that payout’s hold is settled</span>
        ) : (
          <span className="text-subtle">—</span>
        ),
    },
  ];
  return (
    <DataTable
      caption="StarPay notifications filed for a person, newest first: when each arrived, what the platform found, the payout it is about, where it stands, and what can be done about it"
      columns={columns}
      rows={rows}
      rowKey={(r) => r.id}
      empty={empty}
    />
  );
}

/** The tab's own address on Player funds, which System admin links to. */
export const PARKED_NOTIFICATIONS_PATH = '/funds?tab=parked-notifications';

export function ParkedNotifications({
  canRead,
  canMark,
  brandId,
  seesEveryBrand,
  role,
  onMarked,
}: {
  /** transactions.view: the key the list, and the tile counting it, are read with. */
  canRead: boolean;
  /** funds.approve: the payout desk's deciding key, which marking one seen needs. */
  canMark: boolean;
  /** The brand chosen on the funds screen, or null for all of the reader's. */
  brandId: string | null;
  /** Whether the reader's role sees every brand — the one reader shown a notice that names no payout. */
  seesEveryBrand: boolean;
  role: string;
  /** Called once a notification is marked seen, so the tile counting them is read again. */
  onMarked?: () => void;
}) {
  const { showError } = useErrorDialog();
  const { toast } = useToast();
  const [page, setPage] = useState(1);
  /* A brand change starts the list again from its first page, as the queue's does. */
  const [pagedBrand, setPagedBrand] = useState(brandId);
  if (pagedBrand !== brandId) {
    setPagedBrand(brandId);
    setPage(1);
  }
  const [rows, setRows] = useState<ParkedNotificationRow[]>([]);
  const [meta, setMeta] = useState<PageMeta | null>(null);
  const [read, setRead] = useState<'loading' | 'ready' | 'failed'>('loading');
  /** Bumped after a mark, so the list is read again. */
  const [reads, setReads] = useState(0);

  useEffect(() => {
    /* NOT ASKED, rather than asked and refused: the route is transactions.view's, and the refusal
       below is a render that runs after this effect was registered. */
    if (!canRead) return;
    let cancelled = false;
    setRead('loading');
    const search = new URLSearchParams({ page: String(page), perPage: String(PAGE_SIZE) });
    if (brandId) search.set('brandId', brandId);
    api
      .get<{ items: ParkedNotificationRow[]; meta: PageMeta }>(
        `/api/platform/webhooks/parked?${search}`,
      )
      .then((d) => {
        if (cancelled) return;
        setRows(d.items);
        setMeta(d.meta);
        setRead('ready');
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        /* The tab's own list, so a failed read opens the error dialog, as the Payout accounts tab's
           does — and the table says it could not be read, never that nothing waits. */
        setRows([]);
        setMeta(null);
        setRead('failed');
        showError({
          title: 'Could not load the parked notifications',
          message:
            error instanceof ApiRequestError
              ? error.message
              : 'The StarPay notifications filed for a person could not be fetched. Try again shortly.',
          ...(error instanceof ApiRequestError ? { code: error.code } : {}),
        });
      });
    return () => {
      cancelled = true;
    };
  }, [canRead, brandId, page, reads, showError]);

  /* ── Marking one as seen ─────────────────────────────────────────────────── */

  const [marking, setMarking] = useState<ParkedNotificationRow | null>(null);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  const open = (row: ParkedNotificationRow) => {
    setMarking(row);
    setReason('');
    setReasonError(undefined);
  };
  const close = () => {
    setMarking(null);
    setReasonError(undefined);
  };

  const mark = async () => {
    if (marking === null || !reasonReady(reason) || busy) return;
    setBusy(true);
    try {
      await api.post<ParkedNotificationRow>(
        `/api/platform/webhooks/${encodeURIComponent(marking.id)}/seen`,
        { reason: reason.trim() },
      );
      toast('Marked as seen. It no longer counts on the Payout provider tile.');
      close();
      setReads((n) => n + 1);
      onMarked?.();
    } catch (error: unknown) {
      if (error instanceof ApiRequestError && error.fields?.['reason']) {
        setReasonError(error.fields['reason']);
        return;
      }
      /* Read again either way: a refusal here is usually the row having moved — marked by a
         colleague, or matched to a payout since — and the list should show where it stands. */
      setReads((n) => n + 1);
      close();
      showError({
        title: 'The notification was not marked as seen',
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was changed. The list has been read again — check it before trying again.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel title="Parked notifications">
      <div className="notice notice-quiet">
        <Icon name="info" />
        <div>
          When StarPay sends a notification the platform cannot confirm, it is filed here for a person,
          and the Payout provider tile above counts the ones still waiting.{' '}
          <strong>One about a payout</strong> that was left waiting for an operator clears when that
          payout&rsquo;s hold is settled — open it from its reference.{' '}
          <strong>One that names no payout</strong> — a test payout on StarPay&rsquo;s UAT files one, and
          so does a notice meant for another merchant — waits until somebody marks it seen, with a
          reason. Neither moves any money.
          {seesEveryBrand ? null : (
            <>
              {' '}
              A notification that names no payout belongs to no brand, so it is listed for a role that
              sees every brand; {role} sees only the brands it is given.
            </>
          )}
        </div>
      </div>

      {!canRead ? (
        <EmptyState icon="lock-key" title="Parked notifications need transactions.view">
          Reading them needs the transactions.view capability, which {role} does not hold. Nothing was
          read, so nothing here says whether any are waiting.
        </EmptyState>
      ) : read === 'loading' && rows.length === 0 ? (
        <div className="skeleton" style={{ height: '120px', marginTop: 'var(--space-3)' }}>
          &nbsp;
        </div>
      ) : (
        <div style={{ marginTop: 'var(--space-3)' }} aria-busy={read === 'loading'}>
          <ParkedNotificationsTable
            rows={rows}
            canMark={canMark}
            onMark={open}
            empty={
              read === 'failed' ? (
                <EmptyState icon="warning-circle" title="The parked notifications could not be read">
                  The read failed, so this list is empty because nothing came back — not because no
                  notification is waiting. Try again shortly.
                </EmptyState>
              ) : (
                <EmptyState icon="check-circle" title="No notification has been filed for a person">
                  StarPay has sent nothing the platform could not confirm. The read succeeded, so this
                  is a real zero and not a list that failed to load.
                </EmptyState>
              )
            }
          />
          <div className="text-subtle" style={{ fontSize: '12px', paddingTop: 'var(--space-2)' }}>
            {meta
              ? `${formatCount(rows.length)} of ${formatCount(meta.total)} filed for a person · newest first`
              : read === 'failed'
                ? 'the read failed — there is no count to report'
                : 'counting notifications…'}
          </div>
          {meta && meta.total > meta.perPage ? (
            <Pagination page={meta.page} perPage={meta.perPage} total={meta.total} onPage={setPage} />
          ) : null}
        </div>
      )}

      {/* Not dismissible: a stray Escape would throw away a reason already typed. Quiet, because
          marking one moves no money and touches no payout. */}
      <Dialog
        open={marking !== null}
        onClose={close}
        title="Mark this notification as seen?"
        dismissible={false}
        actions={
          <>
            <Button onClick={close} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="primary"
              busy={busy}
              disabled={!reasonReady(reason) || busy}
              onClick={() => void mark()}
            >
              Mark as seen
            </Button>
          </>
        }
      >
        {marking !== null ? (
          <div className="stack">
            <div className="notice notice-quiet">
              <Icon name="info" />
              <div>
                It stops counting as waiting for a person on the Payout provider tile. No payout
                changes, no money moves, and StarPay is not told. Received {at(marking.receivedAt)}:{' '}
                {marking.note}
              </div>
            </div>
            <Field
              label="Reason"
              hint={`At least ${AUDIT_REASON_MIN_LENGTH} characters — what it was, and how you know. It goes on the audit trail with your name.`}
              error={reasonError ?? reasonProblem(reason)}
            >
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  aria-invalid={invalid}
                  minLength={AUDIT_REASON_MIN_LENGTH}
                  maxLength={500}
                  value={reason}
                  onChange={(e) => {
                    setReason(e.target.value);
                    setReasonError(undefined);
                  }}
                  placeholder="A UAT probe disbursement on 12 Oct; no payout of ours was involved."
                />
              )}
            </Field>
          </div>
        ) : null}
      </Dialog>
    </Panel>
  );
}
